import { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import {
  useAppSelector,
  getCatalogConfig,
  getExtraDetails,
  getSearchKey,
  getWorkspaceId,
  useEditOrderMutation,
  dispatch,
  updateProductLineItems,
  ErrorVariants,
  showToast,
  ProductVariant,
  CategoryTreeItem,
  ProductCardVariant,
  useGetProductsByCustomerMutation,
  getAppliedFilters,
  FilterRoute,
  useLazyGetCustomerDetailsQuery,
  useCreatePriceListMutation,
  CriteriaEnum,
  CreatePriceListRequest,
  updateSelectedProducts,
  UOM,
  removeSearchKey,
} from '@zonofi/zono-money-store';
import { OrderProductActionType, SortDirection } from '../../constants';
import { useDebounceSearch, usePaginatedList } from '../../hooks';
import { isEmpty } from 'lodash';
import { useParams } from 'react-router-dom';
import { shallowEqual } from 'react-redux';
import {
  ChipDataItem,
  ChipFilterSection,
  PriceUpdatedProduct,
  ProductPriceUpdateMap,
} from '../../types';
import moment from 'moment';

export const useProductByCustomerList = (props?: {
  customerId?: string;
  onReplaceSuccess?: () => void;
}) => {
  const { onReplaceSuccess = () => {}, customerId = '' } = props || {};
  const { showOutOfStockTag } = useAppSelector(getCatalogConfig);
  const { orderId } = useParams();
  const orderInfo = useAppSelector(getExtraDetails);
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);
  const appliedFilters = useAppSelector(
    getAppliedFilters(FilterRoute.Product),
    shallowEqual
  );
  const searchKey = useAppSelector(getSearchKey);
  const [editRemoveOrderApi, editRemoveOrderResponse] = useEditOrderMutation();
  const [getProductsListApi, getProductsListApiResponse] =
    useGetProductsByCustomerMutation();
  const [getCustomerDetailsApi, customerDetailsApi] =
    useLazyGetCustomerDetailsQuery();
  const [createUpdatePriceListReq, createUpdatePriceListres] =
    useCreatePriceListMutation();

  const [categoryTree, setCategoryTree] = useState<
    CategoryTreeItem | undefined
  >(undefined);
  const [segmentFilters, setSegmentFilters] = useState<ChipFilterSection[]>([]);

  const [selectedCategories, setSelectedCategories] = useState<
    CategoryTreeItem[]
  >([]);
  const [updatedProducts, setUpdatedProducts] = useState<ProductPriceUpdateMap>(
    {}
  );
  const [showFilter, setShowFilter] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [successState, setSuccessState] = useState(false);
  const { debouncedSearchValue } = useDebounceSearch();
  const [selectedSegment, setSelectedSegment] = useState('');
  const [currentProductId, setCurrentProductId] = useState<number | null>(null);

  const lastActionRef = useRef<
    'addProduct' | 'updateOrRemove' | 'replaceProduct' | 'addToCart' | null
  >(null);
  const totalRef = useRef<number | null>(null);
  const initialLoadRef = useRef<boolean>(true);

  const resolvedCustomerId = useMemo(
    () =>
      orderInfo?.customerId && orderInfo.customerId.trim()
        ? orderInfo.customerId
        : customerId,
    [orderInfo?.customerId, customerId]
  );

  useEffect(() => {
    if (!isEmpty(categoryTree) || !isEmpty(selectedCategories)) return;
    const rootCategory = getProductsListApiResponse?.data?.categoryTree?.find(
      (category) => category?.categoryCode === '__root_category__'
    );

    if (!rootCategory || !isEmpty(selectedCategories)) return;

    const hasChildren = rootCategory && rootCategory.children?.length;
    const hasSingleChildWithChildren =
      rootCategory &&
      rootCategory.children?.length === 1 &&
      rootCategory.children[0]?.children?.length &&
      rootCategory.children[0]?.children?.length > 0;
    const hasMultipleChildren =
      rootCategory.children?.length && rootCategory?.children?.length > 1;

    if (hasChildren || hasSingleChildWithChildren || hasMultipleChildren) {
      setCategoryTree(rootCategory);
    }
  }, [
    getProductsListApiResponse?.data?.categoryTree,
    selectedCategories,
    categoryTree,
  ]);

  useEffect(() => {
    const segments = getProductsListApiResponse?.data?.segmentsSummary || [];
    if (segments.length === 0) return;

    const filters: ChipDataItem[] = segments.map(
      ({ segmentId, segmentName }) => ({
        id: segmentId,
        selected: selectedSegment === segmentId,
        value: segmentName,
      })
    );
    setSegmentFilters([
      {
        items: [
          { id: '', selected: selectedSegment === '', value: 'All' },
          ...filters,
        ],
        onItemPress: ({ id }) => setSelectedSegment(id),
      },
    ]);
  }, [getProductsListApiResponse?.data?.segmentsSummary, selectedSegment]);

  const fetchProducts = useCallback(
    (pageParams: { pageNo: number; pageSize: number }) => {
      if (pageParams.pageNo === 1 && !initialLoadRef.current) {
        return;
      }

      const categoryCodes = selectedCategories
        ?.map((i) => i?.categoryCode)
        .filter(Boolean);

      getProductsListApi({
        workspaceId: sellerWorkspaceId,
        params: {
          customerId: resolvedCustomerId,
          ...pageParams,
        },
        body: {
          ...(debouncedSearchValue ? { searchKey: debouncedSearchValue } : {}),
          sortBy: appliedFilters?.sortOptions?.field ?? 'sortOrder',
          sortDirection:
            appliedFilters?.sortOptions?.direction ?? SortDirection.ASCENDING,
          customerId: resolvedCustomerId,
          includeCategoryTreeList: true,
          ...(categoryCodes?.length > 0 ? { categoryCodes } : {}),
          ...(selectedSegment.length > 0
            ? { segmentIds: [selectedSegment] }
            : {}),
          priceListId: orderInfo?.priceListId,
        },
      });

      if (pageParams.pageNo === 1) {
        initialLoadRef.current = false;
      }
    },
    [
      debouncedSearchValue,
      selectedCategories,
      appliedFilters?.sortOptions,
      selectedSegment,
    ]
  );

  const memoizedProducts = useMemo(() => {
    return (
      getProductsListApiResponse?.data?.products?.flatMap(
        ({ productVariants }) => productVariants
      ) ?? []
    );
  }, [getProductsListApiResponse?.data?.products]);

  const {
    data: productsData,
    pageNo,
    lastItemRef,
    resetAll,
    refresh,
    isPageLoading,
    onScroll,
    isEndOfList,
  } = usePaginatedList<ProductVariant>({
    fetchDataApiCall: fetchProducts,
    apiResponseData: memoizedProducts,
    isLoading: getProductsListApiResponse.isLoading,
    uniqueKey: 'id',
    dependencies: [
      debouncedSearchValue,
      selectedCategories,
      appliedFilters,
      selectedSegment,
    ],
    isError: getProductsListApiResponse.isError,
    paginationMeta: {
      startRecord: getProductsListApiResponse?.data?.startRecord || 0,
      endRecord: getProductsListApiResponse?.data?.endRecord || 0,
      totalCount: getProductsListApiResponse?.data?.total || 0,
    },
  });

  useEffect(() => {
    if (
      getProductsListApiResponse?.data?.total &&
      totalRef.current !== getProductsListApiResponse?.data?.total
    ) {
      totalRef.current = getProductsListApiResponse.data.total;
    }
  }, [getProductsListApiResponse?.data?.total]);

  const handleEditOrder = (
    productVariantId: number,
    quantity: number,
    operator?: 'add',
    uom?: UOM
  ) => {
    editRemoveOrderApi({
      workspaceId: sellerWorkspaceId,
      body: {
        customerId: resolvedCustomerId,
        orderId: orderId,
        lines: [
          {
            ...(operator && { operator }),
            productVariantId,
            quantity: uom?.packSize ? quantity * uom?.packSize : quantity,
          },
        ],
        priceListId: orderInfo?.priceListId,
      },
    });
  };

  const onAddProductPress = (product: ProductVariant, uom?: UOM) => {
    lastActionRef.current = 'addProduct';
    setCurrentProductId(product.productVariantId);
    handleEditOrder(product.productVariantId, product.minOrderQty, 'add', uom);
  };

  const onProductRemoveAndUpdate = (
    product: ProductVariant,
    type: OrderProductActionType
  ) => {
    lastActionRef.current = 'updateOrRemove';
    setCurrentProductId(product.productVariantId);

    if (type === OrderProductActionType.REMOVE) {
      handleEditOrder(product.productVariantId, 0);
    } else {
      handleEditOrder(
        product.productVariantId,
        product.newQty as number,
        'add'
      );
    }
  };

  const onReplaceProductPress = (product: ProductVariant) => {
    const isOrderReplace =
      orderInfo?.variantType === ProductCardVariant.ORDER_REPLACE;
    const isOrderMap = orderInfo?.variantType === ProductCardVariant.ORDER_MAP;

    if (!isOrderMap && !isOrderReplace) return;

    const lineItem = {
      productVariantId: product.productVariantId,
      quantity: product.minOrderQty,
      poFileLineId: orderInfo?.poFileLineId,
      ...(isOrderReplace && { orderLineId: orderInfo?.orderLineId }),
    };

    lastActionRef.current = 'replaceProduct';

    editRemoveOrderApi({
      workspaceId: sellerWorkspaceId,
      body: {
        customerId: resolvedCustomerId,
        orderId: orderInfo.orderId ?? orderId,
        poFileId: orderInfo?.poFileId,
        lines: [lineItem],
        priceListId: orderInfo?.priceListId,
      },
    });
  };

  useEffect(() => {
    if (
      editRemoveOrderResponse.isSuccess &&
      editRemoveOrderResponse.data &&
      editRemoveOrderResponse.data?.orderLines?.length > 0
    ) {
      const updatedItems = editRemoveOrderResponse.data.orderLines.map(
        (line) => ({
          productVariantId: line.productVariantId as number,
          productMOQ: line.minOrderQty as number,
          quantity: line.quantity as number,
          ...(lastActionRef.current === 'replaceProduct' && {
            replaceProductVariantId: orderInfo?.productVariantId,
          }),
        })
      );
      setCurrentProductId(null);

      dispatch(updateProductLineItems(updatedItems));

      if (lastActionRef.current === 'addProduct') {
        dispatch(
          showToast({
            message: '1 Product added successfully',
            type: ErrorVariants.SUCCESS,
            canDisplay: true,
            responseStatus: 'Success',
          })
        );
      }

      if (lastActionRef.current === 'replaceProduct') {
        dispatch(
          showToast({
            message: 'Product replaced successfully',
            type: ErrorVariants.SUCCESS,
            canDisplay: true,
            responseStatus: 'Success',
          })
        );
        onReplaceSuccess();
      }
      if (lastActionRef.current === 'addToCart') {
        dispatch(
          showToast({
            message: 'Product added to cart successfully',
            type: ErrorVariants.SUCCESS,
            canDisplay: true,
            responseStatus: 'Success',
          })
        );
      }

      lastActionRef.current = null;
    } else if (editRemoveOrderResponse.isError) {
      setCurrentProductId(null);
      dispatch(
        showToast({
          message: 'Something went wrong!',
          type: ErrorVariants.DANGER,
          canDisplay: true,
          responseStatus: 'Error',
        })
      );
    }
  }, [editRemoveOrderResponse]);

  useEffect(() => {
    if (debouncedSearchValue?.length !== 0) {
      setSelectedCategories([]);
    }
  }, [debouncedSearchValue]);

  useEffect(() => {
    initialLoadRef.current = true;
  }, [
    debouncedSearchValue,
    selectedCategories,
    appliedFilters?.sortOptions,
    selectedSegment,
  ]);

  const onSearchCancel = () => {
    dispatch(removeSearchKey());
  };

  const onUpdatePrice = (item: PriceUpdatedProduct) => {
    const { price, pvId, ptr } = item;
    const id = String(pvId);
    if (!updatedProducts[id]) {
      setUpdatedProducts((prev) => ({ ...prev, [id]: item }));
    } else {
      setUpdatedProducts((prev) => ({
        ...prev,
        [id]: { ...prev[id], price: Number(price), ptr: Number(ptr) },
      }));
    }
  };

  const onSaveCustomerPrices = (customerId: string) => {
    getCustomerDetailsApi({
      sellerWorkspaceId,
      inviteId: customerId,
      includeInactive: true,
    });
  };

  useEffect(() => {
    if (customerDetailsApi.isSuccess && customerDetailsApi.data) {
      const { companyName, priceLists, inviteId } = customerDetailsApi.data;
      const existingPriceList = priceLists?.[0];

      const baseString = companyName.replace(/[^a-zA-Z0-9]/g, '');
      const randomDigits = () =>
        Math.floor(1000 + Math.random() * 9000).toString();

      const name = existingPriceList?.name
        ? existingPriceList.name
        : baseString.slice(0, 2).toUpperCase() + randomDigits();
      const code = existingPriceList?.code
        ? existingPriceList.code
        : baseString.slice(-2).toUpperCase() + randomDigits();

      const priceListItems = Object.values(updatedProducts).map((item) => ({
        productVariantId: item.pvId,
        pts: Number(item.price),
        ptr: Number(item.ptr),
      }));

      const payload: CreatePriceListRequest = {
        name,
        code,
        criteria: CriteriaEnum.CUSTOMER,
        sellerWorkspaceId,
        priceListItems,
        doMerge: true,
        ...(existingPriceList?.code
          ? {
              priceConditions: [{}],
            }
          : {
              priceConditions: [
                {
                  criteria: CriteriaEnum.CUSTOMER,
                  id: inviteId,
                },
              ],
              startDate: moment().format('YYYY-MM-DD'),
              endDate: moment().add(1, 'month').format('YYYY-MM-DD'),
            }),
      };
      createUpdatePriceListReq(payload);
    }
  }, [customerDetailsApi]);

  useEffect(() => {
    if (createUpdatePriceListres.isSuccess && createUpdatePriceListres.data) {
      const updatedProductVariantIds = Object.keys(updatedProducts).map(Number);
      const matchedProducts = productsData
        .filter((product) =>
          updatedProductVariantIds.includes(product.productVariantId)
        )
        .map((product) => {
          const update = updatedProducts[product.productVariantId];
          return {
            ...product,
            price: update.price,
            PTR: update.ptr ?? 0,
          };
        });

      dispatch(updateSelectedProducts(matchedProducts));
      setSuccessState(true);
    } else if (createUpdatePriceListres.isError) {
      dispatch(
        showToast({
          message: 'Error updating prices',
          type: ErrorVariants.DANGER,
          canDisplay: true,
          responseStatus: 'Danger',
        })
      );
    }
  }, [createUpdatePriceListres, productsData, updatedProducts]);

  return {
    productsData,
    total: totalRef.current ?? 0,
    pageNo,
    lastItemRef,
    isPageLoading,
    resetAll,
    onAddProductPress,
    onReplaceProductPress,
    onProductRemoveAndUpdate,
    categoryTree,
    setSelectedCategories,
    onSearchCancel,
    searchKey,
    onScroll,
    isLoading:
      pageNo === 1 &&
      (getProductsListApiResponse.isUninitialized ||
        getProductsListApiResponse.isLoading),
    setShowFilter,
    showFilter,
    setShowSearch,
    showSearch,
    onUpdatePrice,
    updatedProducts,
    onSaveCustomerPrices,
    successState,
    setSuccessState,
    priceLoading:
      customerDetailsApi.isLoading || createUpdatePriceListres.isLoading,
    segmentFilters,
    setSelectedSegment,
    isEndOfList,
    refreshProducts: refresh,
    isReplaceProductLoading: editRemoveOrderResponse?.isLoading || false,
    currentProductId,
    showOutOfStockTag,
  };
};
